/* eslint-disable camelcase */
import { startsWith } from 'lodash'
import type { AxiosResponse, AxiosError } from 'axios'
import type { EActivityTriggerType } from './types/activity-type';

export function isAxiosError(error: any): error is AxiosError {
    if (!error) { return false }
    return error.isAxiosError
}

export function ExceptionFactory<E>(tag: string) {
    const symbol = Symbol.for(tag + 'ExceptionSymbol')
    return {
        symbol,
        mark(error: Record<string, unknown>) {
            return {
                ...error,
                klass: symbol,
            }
        },
        is(error: any): error is AxiosError<E> {
            if (!error) { return false }
            return error.klass === symbol
        },
    }
}

export function ResponseExceptionFactory<E>(tag: string) {
    const symbol = Symbol.for(tag + 'ResponseExceptionSymbol')
    return {
        symbol,
        mark(error: Record<string, unknown>) {
            return {
                ...error,
                klass: symbol,
            }
        },
        is(error: any): error is AxiosResponse<E> {
            if (!error) { return false }
            return error.klass === symbol
        },
    }
}

// #region AsyncException
const asyncCancelErrorSymbol = Symbol.for('$$AsyncCancelError')

export class AsyncCancelError extends Error {
    cls = asyncCancelErrorSymbol
}

export function isAsyncCancelError(e: any): e is AsyncCancelError {
    return e instanceof AsyncCancelError || (e?.cls === asyncCancelErrorSymbol)
}

export interface ICoreDataError {
    numeric_error_code: number,
    error_code: number
    error_params: { [key: string]: string }
    error_message: string
    message_vars: string[]
    originating_service: string
    timestamp: string
}

export interface ICoreDataWrapper<T> {
    status: boolean
    error_code: number
    data: T
    error_params: { [key: string]: string }
}

export const BiHttpException = ExceptionFactory('$$BiHttpException')
/**
 * http code != 200 时的错误
 */
export const HttpFailException = ExceptionFactory<ICoreDataError>('CoreBaseException')

/**
 * http code 200 时的错误
 */
export const HttpSuccessException = ResponseExceptionFactory<ICoreDataWrapper<ICoreDataError>>('CoreBaseDataException')

// #region AnonymousException
const anonymousErrorSymbol = Symbol.for('$$AnonymousError')

export class AnonymousError extends Error {
    cls = anonymousErrorSymbol
}

export function isAnonymousError(e: any) {
    return e instanceof AnonymousError || (e?.cls === anonymousErrorSymbol)
}

// #region HttpStatusError

// #region 请求登录
const loginRequiredErrorSymbol = Symbol.for('$$LoginRequiredError')

export class LoginRequiredError extends Error {
    cls = loginRequiredErrorSymbol
}

export function isLoginRequiredError(e: any) {
    return e instanceof LoginRequiredError || (e?.cls === loginRequiredErrorSymbol)
}

// #endregion

export enum E_HttpStatusErrorType {
    Offline = 'Offline',
    XX500 = 'XX500',
    XX400 = 'XX400',
    Unknown = ''
}

const HttpStatusErrorSymbol = Symbol.for('$$HttpStatusError')

export class HttpStatusError extends Error {
    constructor(public axiosError: AxiosError, public netErrorType: E_HttpStatusErrorType) {
        super('HttpStatusError')
    }

    cls = HttpStatusErrorSymbol
}

export function isHttpStatusError(e: any): e is HttpStatusError {
    return e instanceof HttpStatusError || (e?.cls === HttpStatusErrorSymbol)
}

export function getHttpStatusErrorType(e: AxiosError) {
    const status = e?.response?.status
    if (!status) {
        return E_HttpStatusErrorType.Offline
    }
    if (startsWith(status.toString(), '4')) {
        return E_HttpStatusErrorType.XX400
    }
    if (startsWith(status.toString(), '5')) {
        return E_HttpStatusErrorType.XX500
    }
    return E_HttpStatusErrorType.Unknown;
}

// #endregion
